import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Checkbox, Modal, Select } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import type { ApproveApplicationInput } from "@wmart/shared-types";

interface ApproveFormValues {
  tier: "bronze" | "silver" | "gold";
  allow_card: boolean;
  allow_bank_transfer: boolean;
}

interface Props {
  applicationId: string;
  onApproved: () => void | Promise<void>;
  onClose: () => void;
}

/**
 * Modal for approving a trade-account application.
 *
 * Staff must pick a tier and at least one allowed payment method before
 * the application transitions to "approved". Posts to
 * `POST /v1/admin/applications/:id/approve` with an Idempotency-Key
 * (automatically added by api-client for POST requests).
 *
 * On success the caller's `onApproved()` callback is invoked; the caller
 * is responsible for closing the modal, navigating back, and toasting.
 * On server error an inline message is shown and the modal stays open.
 */
export function ApproveApplicationModal({
  applicationId,
  onApproved,
  onClose,
}: Props) {
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
  } = useForm<ApproveFormValues>({
    defaultValues: {
      tier: "silver",
      allow_card: true,
      allow_bank_transfer: false,
    },
  });

  const allowCard = watch("allow_card");
  const allowBankTransfer = watch("allow_bank_transfer");
  const noMethodSelected = !allowCard && !allowBankTransfer;

  async function onSubmit(values: ApproveFormValues) {
    setServerError(null);

    const allowed_payment_methods: ApproveApplicationInput["allowed_payment_methods"] =
      [];
    if (values.allow_card) allowed_payment_methods.push("card");
    if (values.allow_bank_transfer) allowed_payment_methods.push("bank_transfer");

    if (allowed_payment_methods.length === 0) {
      setServerError("Select at least one payment method.");
      return;
    }

    try {
      await api.post(`/v1/admin/applications/${applicationId}/approve`, {
        tier: values.tier,
        allowed_payment_methods,
      });
      await onApproved();
    } catch (err) {
      if (err instanceof ApiError && err.problem) {
        setServerError(err.problem.title ?? "Approve failed");
      } else {
        setServerError("Approve failed. Please try again.");
      }
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Approve application"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || noMethodSelected}
            loading={isSubmitting}
          >
            Approve &amp; welcome
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Select
          label="Tier"
          error={errors.tier?.message}
          {...register("tier", { required: "Tier is required" })}
        >
          <option value="bronze">Bronze</option>
          <option value="silver">Silver</option>
          <option value="gold">Gold</option>
        </Select>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
            Allowed payment methods
          </legend>
          <Checkbox
            label="Card"
            {...register("allow_card")}
          />
          <Checkbox
            label="Bank transfer"
            {...register("allow_bank_transfer")}
          />
          {noMethodSelected ? (
            <p className="text-body-sm text-danger">
              Select at least one payment method.
            </p>
          ) : null}
        </fieldset>

        {serverError ? (
          <p role="alert" className="rounded-sm bg-danger-soft px-3 py-2 text-body-sm text-danger">
            {serverError}
          </p>
        ) : null}

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
